import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { crowdfundAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Heart, CheckCircle, ArrowRight, ArrowLeft, Users } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function BackUsPage() {
  const [campaign, setCampaign] = useState(null);
  const [backers, setBackers] = useState([]);
  const [pledging, setPledging] = useState(null);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isSuccess = location.pathname.includes('/back-us/success');
  const params = new URLSearchParams(location.search);
  const successPledgeId = params.get('pledge_id');

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([crowdfundAPI.getCampaign(), crowdfundAPI.getBackers(50)]);
        setCampaign(c.data);
        setBackers(b.data.backers || []);
      } catch (err) {
        console.error('Failed to load campaign:', err);
        toast.error('Failed to load campaign');
      }
    })();
  }, []);

  // If returning from Stripe success, poll the pledge status
  useEffect(() => {
    if (!isSuccess || !successPledgeId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await crowdfundAPI.pledgeStatus(successPledgeId);
        if (data.status === 'paid') {
          toast.success('Thank you for backing Divispend!');
          // Refresh campaign totals
          const { data: c } = await crowdfundAPI.getCampaign();
          setCampaign(c);
        } else if (attempts < 6) {
          attempts++;
          setTimeout(poll, 1500);
        }
      } catch (err) { console.error('Pledge status poll failed:', err); }
    };
    poll();
  }, [isSuccess, successPledgeId]);

  const pledgeForTier = async (tier) => {
    if (!user) {
      toast.error('Please sign in first');
      navigate('/login');
      return;
    }
    setPledging(tier.key);
    try {
      const origin = window.location.origin;
      const { data } = await crowdfundAPI.pledge(tier.key, origin);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pledge failed');
      setPledging(null);
    }
  };

  if (!campaign) {
    return <div className="min-h-screen bg-[#111111] flex items-center justify-center text-[#999]">Loading…</div>;
  }

  const pct = Math.min(campaign.progress_pct || 0, 100);

  return (
    <div className="min-h-screen bg-[#111111] text-white" data-testid="back-us-page">
      {/* Header */}
      <header className="bg-[#111111]/80 backdrop-blur-xl border-b border-[#222222] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/"><Button variant="ghost" className="text-[#999] hover:text-white"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Button></Link>
        </div>
      </header>

      {isSuccess && (
        <div className="bg-[#22C55E]/10 border-b border-[#22C55E]/30 py-4 text-center">
          <div className="max-w-3xl mx-auto px-4 flex items-center justify-center gap-2 text-sm">
            <CheckCircle className="w-5 h-5 text-[#22C55E]" weight="fill" />
            <span className="text-white font-semibold">Pledge confirmed.</span>
            <span className="text-[#999]">You're now part of Divispend's founding community.</span>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-4 h-4 text-[#22C55E]" weight="fill" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E]">Back the Future</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
            {campaign.title}
          </h1>
          <p className="text-lg text-[#999] max-w-2xl">{campaign.tagline}</p>
        </div>

        {/* Progress */}
        <div className="bg-gradient-to-br from-[#0D1F17] to-[#111111] border border-[#22C55E]/30 rounded-2xl p-6 sm:p-8 mb-12">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <span className="text-4xl sm:text-5xl font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>
              ${(campaign.raised || 0).toLocaleString()}
              <span className="text-lg text-[#666] font-normal ml-2">/ ${(campaign.goal || 0).toLocaleString()}</span>
            </span>
            <span className="text-2xl text-[#22C55E] font-bold">{pct}%</span>
          </div>
          <div className="h-3 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-[#999]">
            <Users className="w-4 h-4" />
            <strong className="text-white">{campaign.backers || 0}</strong> backers so far
          </div>
        </div>

        {/* Tiers */}
        <h2 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>Choose your tier</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {campaign.tiers.map(tier => (
            <div key={tier.key} className="bg-[#0d0d0d] border border-[#222] rounded-xl p-6 hover:border-[#22C55E]/40 transition-colors flex flex-col" data-testid={`tier-${tier.key}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>{tier.name}</h3>
                <span className="text-2xl font-bold text-[#22C55E]" style={{ fontFamily: 'Cabinet Grotesk' }}>${tier.amount}</span>
              </div>
              <ul className="space-y-1.5 my-4 flex-1">
                {tier.perks.map((perk) => (
                  <li key={perk} className="text-xs text-[#bbb] flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-[#22C55E] mt-0.5 flex-shrink-0" weight="fill" />
                    {perk}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => pledgeForTier(tier)}
                disabled={pledging === tier.key}
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm w-full mt-4"
                data-testid={`pledge-${tier.key}`}
              >
                {pledging === tier.key ? 'Redirecting…' : <>Back at ${tier.amount} <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          ))}
        </div>

        {/* Backers wall */}
        <div className="border border-[#222] rounded-xl p-6">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Recent Backers</h2>
          {backers.length === 0 ? (
            <p className="text-sm text-[#666]">Be the first to back Divispend.</p>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {backers.map((b, i) => (
                <div key={b.pledge_id || `${b.name}-${b.amount}-${i}`} className="bg-[#0d0d0d] border border-[#1f1f1f] rounded-lg px-3 py-2 flex items-center justify-between text-sm">
                  <span className="text-white truncate">{b.name}</span>
                  <span className="text-[#22C55E] font-semibold">${b.amount}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-10 text-xs text-[#666]">
          This is a Kickstarter-style reward campaign. Pledges are not investments and do not represent equity or revenue share.
        </div>
      </div>
    </div>
  );
}
